import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Ban, CalendarCheck, CheckCircle2, ChevronDown, ChevronRight, FileText, Loader2, Pencil, RefreshCw } from 'lucide-react';
import { abrirReporteHorasSemana, type FilaReporte } from '../../utils/reporteHorasSemana';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useAppPermisos } from '../../hooks/useAppPermisos';
import { useEsSuperUsuario } from '../../hooks/useEsSuperUsuario';
import {
  btnDanger, btnPrimary, btnSecondary, cardCls, inputCls, labelCls, pageSubtitleCls, pageTitleCls,
  tableWrapCls, tdCls, thCls, theadCls, trCls,
} from '../../components/ui';

// ─── Cierre semanal de finca ─────────────────────────────────────────────────
// Semana SÁBADO a VIERNES; la planilla se fecha ese viernes (como VFP). Toma las
// boletas de labores APROBADAS de la semana; por persona y por día suma las
// horas de todas sus boletas: hasta la jornada del día son ordinarias, el resto
// extra a 1.5 (la finca paga normal, sin tope semanal ni gratificación). Solo
// labores por hora. El cálculo lo hace la base de datos (finca_preparar_cierre)
// y al aprobarlo se crea la planilla semanal ya armada en el ERP y las boletas
// de la semana quedan bloqueadas (finca_aprobar_cierre).

type EstadoCierre = 'borrador' | 'aprobado' | 'anulado';
interface Cierre {
  id: string; semana: string; fecha_inicio: string; fecha_fin: string; fecha_planilla: string; estado: EstadoCierre;
  personas: number; boletas: number; boletas_pendientes: number; lineas_excluidas: number;
  horas_total: number; horas_ordinarias: number; horas_extra: number; horas_gratificacion: number; total_devengado: number;
  periodo_id: number | null; preparado_por_nombre: string | null; aprobado_por_nombre: string | null; aprobado_at: string | null;
}
interface Linea {
  id: string; colaborador_id: number | null; trabajador_codigo: string | null; trabajador_nombre: string; dias: number;
  horas_total: number; horas_ordinarias: number; horas_extra: number; tarifa_hora: number;
  monto_ordinario: number; monto_extra: number; total_devengado: number;
  es_operador: boolean; total_acordado: number | null; total_ajustado: boolean; horas_extra_reales: number;
  horas_gratificacion: number; monto_gratificacion: number; observacion: string | null;
  dias_incapacidad: number; horas_incapacidad: number; monto_incapacidad: number;
}

const ESTADO_LABEL: Record<EstadoCierre, string> = { borrador: 'Preparado (sin aprobar)', aprobado: 'Aprobado y enviado a Planilla', anulado: 'Anulado' };
const ESTADO_COLOR: Record<EstadoCierre, string> = { borrador: '#f59e0b', aprobado: '#22c55e', anulado: '#ef4444' };
const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

function hoyCR() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Costa_Rica' }).format(new Date());
}
function addDias(fecha: string, dias: number) {
  const d = new Date(`${fecha}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}
// Sábado en que inicia la semana de finca (sábado a viernes) que contiene la fecha.
function sabadoDeSemana(fecha: string) {
  const dow = new Date(`${fecha}T12:00:00Z`).getUTCDay(); // 0 dom … 6 sáb
  return addDias(fecha, -((dow - 6 + 7) % 7));
}
function fmtFecha(s: string | null | undefined) {
  if (!s) return '—';
  const [y, m, d] = s.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}
const diaSemana = (s: string) => DIAS[new Date(`${s}T12:00:00Z`).getUTCDay()];
function fmtH(n: number) {
  const v = Number(n || 0);
  const h = Math.floor(v + 1e-9);
  const m = Math.round((v - h) * 60);
  return m === 60 ? `${h + 1}:00` : `${h}:${String(m).padStart(2, '0')}`;
}
const fmtCRC = (n: number) => `₡${Number(n || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Confirmación propia (regla: nunca alert/confirm del navegador).
function Confirmar({ titulo, texto, peligro, onSi, onNo }: { titulo: string; texto: string; peligro?: boolean; onSi: () => void; onNo: () => void }) {
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4" onMouseDown={onNo}>
      <div className={`${cardCls} w-full max-w-md p-5 shadow-2xl`} onMouseDown={e => e.stopPropagation()}>
        <div className="mb-2 text-sm font-semibold text-ink">{titulo}</div>
        <p className="mb-5 text-sm leading-relaxed text-ink-muted">{texto}</p>
        <div className="flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onNo}>Cancelar</button>
          <button type="button" className={peligro ? btnDanger : btnPrimary} onClick={onSi}>Confirmar</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

  // Reporte de Horas para Cálculo de Planilla (formato VFP): por día, horas
  // ordinarias / extra (jornada del día, todas las boletas de la persona) y los
  // totales de la semana según el c.
const MARCA: Record<string, string> = { incapacidad: 'Inc', ausente: 'Aus', permiso: 'Per', vacaciones: 'Vac' };
const NOMBRE_DIA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
// Sirve también a mitad de semana: si la semana no tiene cierre aprobado, primero
// recalcula el resumen en estado "preparado (sin aprobar)" — no cierra boletas ni
// envía nada a Planilla. Devuelve un mensaje de error o null.
export async function generarReporteHorasFinca(empresaId: number, inicio: string, usuarioNombre: string): Promise<string | null> {
    const ventana = window.open('', '_blank');
    if (!ventana) return 'El navegador bloqueó la ventana del reporte. Permita ventanas emergentes para este sitio.';
    ventana.document.write('<p style="font-family:sans-serif;padding:20px">Generando reporte…</p>');
    const buscar = async () => (await supabase.from('finca_cierres').select('*').eq('empresa_id', empresaId)
      .eq('fecha_inicio', inicio).neq('estado', 'anulado').maybeSingle()).data as Cierre | null;
    let cierre = await buscar();
    if (!cierre || cierre.estado === 'borrador') {
      const { error: e } = await supabase.rpc('finca_preparar_cierre', { p_empresa_id: empresaId, p_fecha_inicio: inicio, p_preparado_por: usuarioNombre || null });
      if (e) { ventana.close(); return e.message; }
      cierre = await buscar();
    }
    if (!cierre) { ventana.close(); return 'No se pudo calcular la semana.'; }
    const c: Cierre = cierre;
    const { data: lin } = await supabase.from('finca_cierre_lineas').select('*').eq('cierre_id', c.id).order('trabajador_nombre');
    const lineas = (lin ?? []) as Linea[];
    if (!lineas.length) { ventana.close(); return 'Esa semana todavía no tiene boletas con horas.'; }
    const [{ data: emp }, { data: bols }] = await Promise.all([
      supabase.from('empresas').select('nombre').eq('id', empresaId).maybeSingle(),
      supabase.from('campo_boletas').select('id, fecha').eq('empresa_id', empresaId).in('estado', ['borrador', 'pendiente', 'revisada', 'aprobada'])
        .gte('fecha', c.fecha_inicio).lte('fecha', c.fecha_fin)
        .or(`cierre_semanal_id.is.null,cierre_semanal_id.eq.${c.id}`),
    ]);
    const fechaDe = Object.fromEntries(((bols ?? []) as { id: string; fecha: string }[]).map(b => [b.id, b.fecha]));
    const ids = Object.keys(fechaDe);
    const { data: t } = ids.length
      ? await supabase.from('campo_boleta_trabajadores')
          .select('boleta_id, trabajador_codigo, trabajador_nombre, labor_id, horas_ordinarias, horas_extra, jornada_ordinaria_horas, estado_jornada, estado_asistencia')
          .in('boleta_id', ids)
      : { data: [] };
    const filasT = (t ?? []) as { boleta_id: string; trabajador_codigo: string | null; trabajador_nombre: string | null; labor_id: string | null; horas_ordinarias: number; horas_extra: number; jornada_ordinaria_horas: number | null; estado_jornada: string | null; estado_asistencia: string | null }[];
    const laborIds = Array.from(new Set(filasT.map(r => r.labor_id).filter(Boolean))) as string[];
    const { data: labs } = laborIds.length ? await supabase.from('campo_labores').select('id, modalidad_default').in('id', laborIds) : { data: [] };
    const modalidad = Object.fromEntries(((labs ?? []) as { id: string; modalidad_default: string }[]).map(l => [l.id, l.modalidad_default]));
    // Por persona y día: horas totales y jornada; luego ordinarias / extra del día.
    // prod: horas en labores por producción — se muestran como dato, no se pagan por hora.
    const acum: Record<string, Record<string, { horas: number; jornada: number; prod: number; marca?: string; vacaciones?: boolean }>> = {};
    const nombreDe: Record<string, string> = {};
    for (const r of filasT) {
      const cod = (r.trabajador_codigo || '').trim();
      if (!cod) continue;
      const f = fechaDe[r.boleta_id];
      const d = ((acum[cod] ??= {})[f] ??= { horas: 0, jornada: 0, prod: 0 });
      if (r.trabajador_nombre) nombreDe[cod] = r.trabajador_nombre;
      if (r.estado_asistencia && MARCA[r.estado_asistencia]) d.marca = MARCA[r.estado_asistencia];
      if (r.estado_asistencia === 'vacaciones') { d.vacaciones = true; continue; }
      const esHora = !r.labor_id || (modalidad[r.labor_id] ?? 'hora') === 'hora';
      if (r.estado_jornada === 'rechazada' || r.estado_asistencia === 'ausente') continue;
      if (!esHora) { d.prod += Number(r.horas_ordinarias || 0) + Number(r.horas_extra || 0); continue; }
      d.horas += Number(r.horas_ordinarias || 0) + Number(r.horas_extra || 0);
      d.jornada = Math.max(d.jornada, Number(r.jornada_ordinaria_horas || 0) || 8);
    }
    const filas: FilaReporte[] = lineas.map(l => {
      const dias: FilaReporte['dias'] = {};
      for (const [f, d] of Object.entries(acum[(l.trabajador_codigo || '').trim()] ?? {})) {
        if (d.vacaciones) { dias[f] = { nor: 8, ext: 0, marca: 'Vac', prod: d.prod }; continue; }
        const nor = Math.min(d.horas, l.es_operador ? 8 : (d.jornada || 8));
        dias[f] = { nor, ext: Math.max(d.horas - nor, 0), marca: d.marca, prod: d.prod };
      }
      return {
        codigo: l.trabajador_codigo || '', nombre: l.trabajador_nombre, dias,
        normal: Number(l.horas_ordinarias), extra: Number(l.horas_extra), gratif: Number(l.horas_gratificacion),
        salNormal: Number(l.monto_ordinario), salExtra: Number(l.monto_extra), salGratif: Number(l.monto_gratificacion),
        incap: Number(l.monto_incapacidad), devengado: Number(l.total_devengado),
        // Operador de salario fijo (semana completa): 48 HN + 8 HE fijas; lo trabajado sobre 48 h va como gratificación.
        nota: (() => {
          if (!l.es_operador || Number(l.dias) < 6) return undefined;
          const grat = Number(l.horas_gratificacion);
          return `Salario fijo: ${fmtH(Number(l.horas_extra))} extras fijas${grat > 0 ? ` + ${fmtH(grat)} de extras trabajadas (gratificación)` : ''}`;
        })(),
      };
    });
    // Personas que esta semana solo tienen labores por producción: no están en el
    // cierre (no se pagan por hora) pero sus horas se muestran igual.
    const enCierre = new Set(lineas.map(l => (l.trabajador_codigo || '').trim()));
    for (const [cod, porDia] of Object.entries(acum)) {
      if (enCierre.has(cod) || !Object.values(porDia).some(d => d.prod > 0)) continue;
      const dias: FilaReporte['dias'] = {};
      for (const [f, d] of Object.entries(porDia)) if (d.prod > 0 || d.marca) dias[f] = { nor: 0, ext: 0, marca: d.marca, prod: d.prod };
      filas.push({ codigo: cod, nombre: nombreDe[cod] || cod, dias, normal: 0, extra: 0, gratif: 0,
        salNormal: 0, salExtra: 0, salGratif: 0, incap: 0, devengado: 0, nota: 'Solo labores por producción (se pagan aparte)' });
    }
    const dias = Array.from({ length: 7 }, (_, i) => {
      const f = addDias(c.fecha_inicio, i);
      return { fecha: f, etiqueta: NOMBRE_DIA[new Date(`${f}T12:00:00Z`).getUTCDay()] };
    });
    const ok = abrirReporteHorasSemana({
      empresa: (emp as { nombre: string } | null)?.nombre ?? '', grupo: 'Costo - Finca',
      desde: c.fecha_inicio, hasta: c.fecha_fin, fechaPlanilla: c.fecha_planilla,
      estado: c.estado === 'aprobado' ? 'Cierre aprobado y enviado a Planilla' : 'PARCIAL — sin cerrar (con lo capturado hasta hoy)',
      dias, filas, ventana,
    });
    return ok ? null : 'No se pudo abrir el reporte.';
}

// Karen corrige el total de un operador en una semana incompleta.
function CorregirTotal({ linea, onGuardar, onCerrar }: { linea: Linea; onGuardar: (total: number, obs: string) => void; onCerrar: () => void }) {
  const [total, setTotal] = useState(String(linea.total_acordado ?? ''));
  const [obs, setObs] = useState(linea.observacion ?? '');
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4" onMouseDown={onCerrar}>
      <div className={`${cardCls} w-full max-w-md p-5 shadow-2xl`} onMouseDown={e => e.stopPropagation()}>
        <div className="mb-1 text-sm font-semibold text-ink">Total de la semana — {linea.trabajador_nombre}</div>
        <p className="mb-4 text-xs text-ink-muted">Operador de máquina. Use esto si no trabajó la semana completa; el total se convierte a 48 h + extras + gratificación.</p>
        <label className={labelCls}>Total semanal a pagar (CRC)</label>
        <input autoFocus type="number" className={inputCls} value={total} onChange={e => setTotal(e.target.value)} />
        <label className={`${labelCls} mt-3`}>Motivo</label>
        <input className={inputCls} value={obs} onChange={e => setObs(e.target.value)} placeholder="Ej. trabajó 4 días" />
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onCerrar}>Cancelar</button>
          <button type="button" className={btnPrimary} disabled={!(Number(total) >= 0) || total === ''} onClick={() => onGuardar(Number(total), obs)}>Guardar</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function CierreSemanalFincaPage({ usuarioNombre = '' }: { usuarioNombre?: string }) {
  const empresaId = useEmpresaId();
  const permisos = useAppPermisos();
  const { esSuperUsuario } = useEsSuperUsuario();
  const puedePreparar = esSuperUsuario || permisos.perm_cierre_planilla || permisos.perm_vob || permisos.perm_boletas_labores;
  const puedeAprobar = esSuperUsuario || permisos.perm_cierre_planilla;

  // Por defecto, la última semana completa (el sábado de hace una semana).
  const [semana, setSemana] = useState(addDias(sabadoDeSemana(hoyCR()), -7));
  const [cierre, setCierre] = useState<Cierre | null>(null);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [resumenSemana, setResumenSemana] = useState({ abiertas: 0 });
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [confirmar, setConfirmar] = useState<null | { titulo: string; texto: string; peligro?: boolean; accion: () => void }>(null);
  const [corrigiendo, setCorrigiendo] = useState<Linea | null>(null);
  const [departamentos, setDepartamentos] = useState<{ id: number; nombre: string }[]>([]);
  const [deptoFiltro, setDeptoFiltro] = useState<string>('');
  const [deptoDeColab, setDeptoDeColab] = useState<Record<number, number>>({});
  const [expandido, setExpandido] = useState<Set<string>>(new Set());
  type Excluida = { fecha: string; boleta: string; trabajador: string; labor: string; modalidad: string; horas: number; monto: number };
  const [excluidas, setExcluidas] = useState<Excluida[] | null>(null);
  const [cargandoExcl, setCargandoExcl] = useState(false);
  const [diasPorCodigo, setDiasPorCodigo] = useState<Record<string, { fecha: string; horas: number; nor: number; ext: number; marca?: string }[]>>({});

  const fin = addDias(semana, 6);

  // Qué líneas dejó fuera el cierre: labores que no se pagan por hora (producción / monto fijo / tarea).
  const verExcluidas = async () => {
    if (!cierre) return;
    setCargandoExcl(true); setError('');
    try {
      const { data: bols, error: e1 } = await supabase.from('campo_boletas').select('id, numero, fecha')
        .eq('empresa_id', empresaId).in('estado', ['borrador', 'pendiente', 'revisada', 'aprobada'])
        .gte('fecha', cierre.fecha_inicio).lte('fecha', cierre.fecha_fin)
        .or(`cierre_semanal_id.is.null,cierre_semanal_id.eq.${cierre.id}`);
      if (e1) throw e1;
      const bol = Object.fromEntries(((bols ?? []) as { id: string; numero: string; fecha: string }[]).map(b => [b.id, b]));
      const ids = Object.keys(bol);
      const res: Excluida[] = [];
      for (let k = 0; k < ids.length; k += 100) {
        const { data: t, error: e2 } = await supabase.from('campo_boleta_trabajadores')
          .select('boleta_id, trabajador_codigo, trabajador_nombre, horas_ordinarias, horas_extra, monto_calculado, labor:campo_labores!inner(nombre, modalidad_default)')
          .in('boleta_id', ids.slice(k, k + 100)).neq('labor.modalidad_default', 'hora');
        if (e2) throw e2;
        for (const r of (t ?? []) as any[]) {
          const lab = Array.isArray(r.labor) ? r.labor[0] : r.labor;
          if (!lab) continue;
          res.push({
            fecha: bol[r.boleta_id]?.fecha ?? '', boleta: bol[r.boleta_id]?.numero ?? '',
            trabajador: `${r.trabajador_codigo ? r.trabajador_codigo + ' ' : ''}${r.trabajador_nombre ?? ''}`.trim(),
            labor: lab.nombre, modalidad: lab.modalidad_default,
            horas: Number(r.horas_ordinarias || 0) + Number(r.horas_extra || 0), monto: Number(r.monto_calculado || 0),
          });
        }
      }
      res.sort((a, b) => a.fecha.localeCompare(b.fecha) || a.trabajador.localeCompare(b.trabajador, 'es'));
      setExcluidas(res);
    } catch (e: any) {
      setError(e?.message ?? 'No se pudieron consultar las líneas excluidas.');
    } finally { setCargandoExcl(false); }
  };

  const cargar = useCallback(async () => {
    setLoading(true); setError(''); setExpandido(new Set());
    const [{ data: c }, { data: bols }, { data: deptos }] = await Promise.all([
      supabase.from('finca_cierres').select('*').eq('empresa_id', empresaId).eq('fecha_inicio', semana).neq('estado', 'anulado').maybeSingle(),
      supabase.from('campo_boletas').select('estado').eq('empresa_id', empresaId).gte('fecha', semana).lte('fecha', addDias(semana, 6)),
      supabase.from('pl_departamentos').select('id, nombre').eq('empresa_id', empresaId).order('nombre'),
    ]);
    setDepartamentos((deptos ?? []) as { id: number; nombre: string }[]);
    const lista = (bols ?? []) as { estado: string }[];
    setResumenSemana({
      abiertas: lista.filter(b => !['anulada', 'rechazada'].includes(b.estado)).length,
    });
    setCierre((c ?? null) as Cierre | null);
    if (c) {
      const { data: l } = await supabase.from('finca_cierre_lineas').select('*').eq('cierre_id', (c as Cierre).id).order('trabajador_nombre');
      setLineas((l ?? []) as Linea[]);
      const colabIds = ((l ?? []) as Linea[]).map(x => x.colaborador_id).filter(Boolean) as number[];
      if (colabIds.length) {
        const { data: colabs } = await supabase.from('pl_colaboradores').select('id, departamento_id').in('id', colabIds);
        const mapa: Record<number, number> = {};
        for (const co of (colabs ?? []) as { id: number; departamento_id: number | null }[]) {
          if (co.departamento_id) mapa[co.id] = co.departamento_id;
        }
        setDeptoDeColab(mapa);
      } else {
        setDeptoDeColab({});
      }
      // Detalle diario por trabajador (para acordeón)
      const { data: bols2 } = await supabase.from('campo_boletas')
        .select('id, fecha').eq('empresa_id', empresaId)
        .in('estado', ['borrador', 'pendiente', 'revisada', 'aprobada'])
        .gte('fecha', semana).lte('fecha', addDias(semana, 6))
        .or(`cierre_semanal_id.is.null,cierre_semanal_id.eq.${(c as Cierre).id}`);
      const fechaMap = Object.fromEntries(((bols2 ?? []) as { id: string; fecha: string }[]).map(b => [b.id, b.fecha]));
      const bolIds2 = Object.keys(fechaMap);
      if (bolIds2.length) {
        const { data: trab2 } = await supabase.from('campo_boleta_trabajadores')
          .select('boleta_id, trabajador_codigo, horas_ordinarias, horas_extra, jornada_ordinaria_horas, estado_jornada, estado_asistencia')
          .in('boleta_id', bolIds2);
        const acum2: Record<string, Record<string, { horas: number; jornada: number; marca?: string }>> = {};
        for (const r of (trab2 ?? []) as any[]) {
          const cod = (r.trabajador_codigo || '').trim();
          if (!cod) continue;
          const f = fechaMap[r.boleta_id];
          if (!f) continue;
          const d = ((acum2[cod] ??= {})[f] ??= { horas: 0, jornada: 0 });
          if (r.estado_asistencia && MARCA[r.estado_asistencia]) d.marca = MARCA[r.estado_asistencia];
          if (r.estado_jornada === 'rechazada' || r.estado_asistencia === 'ausente') continue;
          if (r.estado_asistencia === 'vacaciones') { d.marca = 'Vac'; continue; }
          d.horas += Number(r.horas_ordinarias || 0) + Number(r.horas_extra || 0);
          d.jornada = Math.max(d.jornada, Number(r.jornada_ordinaria_horas || 0) || 8);
        }
        const fechas7 = Array.from({ length: 7 }, (_, i) => addDias(semana, i));
        const operadores = new Set(((l ?? []) as Linea[]).filter(x => x.es_operador).map(x => (x.trabajador_codigo || '').trim()));
        const res: Record<string, { fecha: string; horas: number; nor: number; ext: number; marca?: string }[]> = {};
        for (const [cod, porFecha] of Object.entries(acum2)) {
          const jornadaFija = operadores.has(cod) ? 8 : 0;
          res[cod] = fechas7.map(f => {
            const d = porFecha[f];
            if (!d || (d.horas === 0 && !d.marca)) return { fecha: f, horas: 0, nor: 0, ext: 0 };
            if (d.marca && d.horas === 0) return { fecha: f, horas: 0, nor: 0, ext: 0, marca: d.marca };
            const jornada = jornadaFija || d.jornada || 8;
            const nor = Math.min(d.horas, jornada);
            return { fecha: f, horas: d.horas, nor, ext: Math.max(d.horas - nor, 0), marca: d.marca };
          });
        }
        setDiasPorCodigo(res);
      } else {
        setDiasPorCodigo({});
      }
    } else {
      setLineas([]); setDeptoDeColab({}); setDiasPorCodigo({});
    }
    setLoading(false);
  }, [empresaId, semana]);

  useEffect(() => { void cargar(); }, [cargar]);

  async function preparar() {
    setProcesando(true); setError(''); setAviso('');
    const { error: e } = await supabase.rpc('finca_preparar_cierre', { p_empresa_id: empresaId, p_fecha_inicio: semana, p_preparado_por: usuarioNombre || null });
    setProcesando(false);
    if (e) { setError(e.message); return; }
    void cargar();
  }

  async function aprobar() {
    if (!cierre) return;
    setProcesando(true); setError(''); setAviso('');
    const { error: e } = await supabase.rpc('finca_aprobar_cierre', { p_cierre_id: cierre.id, p_aprobado_por: usuarioNombre || null });
    setProcesando(false);
    if (e) { setError(e.message); return; }
    setAviso(`Semana ${cierre.semana} cerrada. La planilla quedó en Planilla del ERP para calcular deducciones y cerrar.`);
    void cargar();
  }

  async function anular() {
    if (!cierre) return;
    setProcesando(true); setError(''); setAviso('');
    const { error: e } = await supabase.rpc('finca_anular_cierre', { p_cierre_id: cierre.id });
    setProcesando(false);
    if (e) { setError(e.message); return; }
    setAviso(`Cierre de la semana ${cierre.semana} anulado. Las boletas vuelven a poder corregirse.`);
    void cargar();
  }

  async function corregirTotal(l: Linea, total: number, obs: string) {
    setCorrigiendo(null); setError(''); setAviso('');
    const { error: e } = await supabase.rpc('finca_ajustar_operador', { p_linea_id: l.id, p_total: total, p_observacion: obs || null });
    if (e) { setError(e.message); return; }
    void cargar();
  }

  async function verReporte() {
    setError(''); setAviso('');
    const e = await generarReporteHorasFinca(empresaId, semana, usuarioNombre);
    if (e) setError(e);
    void cargar();
  }

  const lineasFiltradas = deptoFiltro
    ? lineas.filter(l => l.colaborador_id && deptoDeColab[l.colaborador_id] === Number(deptoFiltro))
    : lineas;
  const sinFicha = lineas.filter(l => !l.colaborador_id || !(Number(l.tarifa_hora) > 0) || !(Number(l.total_devengado) > 0));

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className={pageTitleCls}>Cierre semanal de finca</h1>
        <p className={pageSubtitleCls}>Planilla semanal de Costo - Finca (sábado a viernes) desde las boletas de labores de la semana</p>
      </div>

      <div className={`${cardCls} flex flex-wrap items-end gap-3 p-4`}>
        <div>
          <label className={labelCls}>Semana a cerrar (cualquier día)</label>
          <input type="date" className={inputCls} value={semana} onChange={e => e.target.value && setSemana(sabadoDeSemana(e.target.value))} />
        </div>
        {departamentos.length > 0 && (
          <div>
            <label className={labelCls}>Departamento</label>
            <select className={inputCls} value={deptoFiltro} onChange={e => setDeptoFiltro(e.target.value)}>
              <option value="">Todos</option>
              {departamentos.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
            </select>
          </div>
        )}
        <div className="pb-2 text-xs text-ink-muted">
          {diaSemana(semana)} {fmtFecha(semana)} al {diaSemana(fin)} {fmtFecha(fin)} · planilla del viernes {fmtFecha(fin)} ·
          {' '}{resumenSemana.abiertas} boleta(s)
        </div>
        <div className="ml-auto flex gap-2">
          <button type="button" className={btnSecondary} onClick={() => setSemana(addDias(semana, -7))}>← Anterior</button>
          <button type="button" className={btnSecondary} onClick={() => setSemana(addDias(semana, 7))}>Siguiente →</button>
        </div>
      </div>

      <div className={`${cardCls} flex flex-wrap items-center gap-3 p-4`}>
        {cierre ? (
          <span className="rounded px-2 py-1 text-xs font-semibold" style={{ background: `${ESTADO_COLOR[cierre.estado]}22`, color: ESTADO_COLOR[cierre.estado] }}>
            {ESTADO_LABEL[cierre.estado]}
          </span>
        ) : <span className="text-xs text-ink-faint">Semana sin cierre.</span>}
        {cierre && (
          <span className="text-xs text-ink-muted">
            {cierre.personas} persona(s) · {cierre.boletas} boleta(s) · {fmtH(cierre.horas_ordinarias)} h ordinarias · {fmtH(cierre.horas_extra)} h extra{Number(cierre.horas_gratificacion) > 0 ? ` · ${fmtH(cierre.horas_gratificacion)} h gratificación` : ''} · {fmtCRC(cierre.total_devengado)}
            {cierre.estado === 'aprobado' && cierre.aprobado_por_nombre ? ` · aprobado por ${cierre.aprobado_por_nombre}` : ''}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          {puedePreparar && (!cierre || cierre.estado === 'borrador') && (
            <button type="button" className={`${btnSecondary} flex items-center gap-1.5`} disabled={procesando || resumenSemana.abiertas === 0}
              onClick={() => (cierre
                ? setConfirmar({ titulo: 'Recalcular cierre', texto: 'Se va a recalcular el cierre con las boletas actuales de la semana. ¿Continuar?', accion: preparar })
                : void preparar())}>
              {procesando ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {cierre ? 'Recalcular' : 'Preparar cierre'}
            </button>
          )}
          {(puedePreparar || cierre?.estado === 'aprobado') && (
            <button type="button" className={`${btnSecondary} flex items-center gap-1.5`} onClick={() => void verReporte()} disabled={procesando}>
              <FileText size={13} /> Reporte de horas
            </button>
          )}
          {puedeAprobar && cierre?.estado === 'borrador' && (
            <button type="button" className={`${btnPrimary} flex items-center gap-1.5`} disabled={procesando || sinFicha.length > 0}
              onClick={() => setConfirmar({
                titulo: 'Aprobar cierre semanal',
                texto: `¿Aprobar el cierre de la semana ${cierre.semana} y enviarlo a Planilla? ${cierre.personas} persona(s), total devengado ${fmtCRC(cierre.total_devengado)}. Las boletas de la semana quedan bloqueadas y la planilla llega armada al ERP con fecha ${fmtFecha(cierre.fecha_planilla)}.`,
                accion: aprobar,
              })}>
              <CheckCircle2 size={13} /> Aprobar y enviar a Planilla
            </button>
          )}
          {puedeAprobar && cierre?.estado === 'aprobado' && (
            <button type="button" className={`${btnDanger} flex items-center gap-1.5`} disabled={procesando}
              onClick={() => setConfirmar({
                titulo: 'Anular cierre semanal', peligro: true,
                texto: `¿Anular el cierre de la semana ${cierre.semana}? Se borra la planilla de esa semana en el ERP (solo si aún no está cerrada allá) y las boletas vuelven a poder corregirse.`,
                accion: anular,
              })}>
              <Ban size={13} /> Anular cierre
            </button>
          )}
        </div>
      </div>

      {error && <div className="rounded border border-red-900 bg-red-950/50 px-3 py-2 text-xs text-red-300">{error}</div>}
      {aviso && <div className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-xs text-green-300">{aviso}</div>}
      {cierre && cierre.estado === 'borrador' && cierre.boletas_pendientes > 0 && (
        <div className="flex items-start gap-2 rounded border border-amber-700/50 bg-amber-950/40 px-3 py-2 text-xs text-amber-300">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {cierre.boletas_pendientes} boleta(s) de esta semana todavía no están aprobadas y no entran en el cierre. Apruébelas y use "Recalcular".
        </div>
      )}
      {cierre && cierre.lineas_excluidas > 0 && (
        <div className="flex items-start gap-2 rounded border border-amber-700/50 bg-amber-950/40 px-3 py-2 text-xs text-amber-300">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{cierre.lineas_excluidas} línea(s) de labores por producción o monto fijo quedaron fuera: por ahora el cierre solo paga labores por hora.</span>
          <button type="button" className="ml-auto shrink-0 rounded border border-amber-600/60 px-2 py-0.5 font-semibold hover:bg-amber-900/40" onClick={() => void verExcluidas()} disabled={cargandoExcl}>
            {cargandoExcl ? 'Buscando…' : 'Ver cuáles'}
          </button>
        </div>
      )}
      {sinFicha.length > 0 && (
        <div className="flex items-start gap-2 rounded border border-amber-700/50 bg-amber-950/40 px-3 py-2 text-xs text-amber-300">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {sinFicha.length} persona(s) sin ficha en Planilla, sin tarifa por hora o (operadores) sin salario diario especial / total semanal: {sinFicha.map(l => l.trabajador_nombre).join(', ')}. RRHH debe completar su ficha en Planilla del ERP; luego use "Recalcular".
        </div>
      )}

      <div className={tableWrapCls}>
        <table className="w-full">
          <thead className={theadCls}>
            <tr>
              <th className={thCls}>Cód.</th><th className={thCls}>Colaborador</th><th className={`${thCls} text-right`}>Días</th>
              <th className={`${thCls} text-right`}>Total h</th><th className={`${thCls} text-right`}>Ordinarias</th><th className={`${thCls} text-right`}>Extra</th>
              <th className={`${thCls} text-right`}>Gratif.</th><th className={`${thCls} text-right`}>Tarifa</th><th className={`${thCls} text-right`}>₡ Ordinario</th>
              <th className={`${thCls} text-right`}>₡ Extra</th><th className={`${thCls} text-right`}>₡ Gratif.</th>
              <th className={`${thCls} text-right`} title="Subsidio por incapacidad (no salarial)">₡ Incap.</th><th className={`${thCls} text-right`}>₡ Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className={`${tdCls} py-6 text-center`} colSpan={13}><Loader2 size={16} className="inline animate-spin" /> Cargando…</td></tr>
            ) : lineasFiltradas.length === 0 ? (
              <tr><td className={`${tdCls} py-6 text-center`} colSpan={13}>
                {cierre ? (deptoFiltro ? 'Ningún colaborador en este departamento.' : 'El cierre no tiene horas.') : resumenSemana.abiertas === 0 ? 'No hay boletas en esta semana.' : 'Prepare el cierre para ver el resumen de la semana por persona.'}
              </td></tr>
            ) : lineasFiltradas.map(l => {
              const cod = (l.trabajador_codigo || '').trim();
              const isExp = expandido.has(l.id);
              const dias = diasPorCodigo[cod];
              return (
                <React.Fragment key={l.id}>
                  <tr className={`${trCls} cursor-pointer select-none`} onClick={() => setExpandido(prev => { const s = new Set(prev); s.has(l.id) ? s.delete(l.id) : s.add(l.id); return s; })}>
                    <td className={`${tdCls} font-mono`}>
                      <span className="inline-flex items-center gap-1">
                        {isExp ? <ChevronDown size={12} className="text-ink-faint" /> : <ChevronRight size={12} className="text-ink-faint" />}
                        {l.trabajador_codigo || '—'}
                      </span>
                    </td>
                    <td className={`${tdCls} font-semibold text-ink`}>
                      {l.trabajador_nombre}
                      {l.es_operador && puedeAprobar && cierre?.estado === 'borrador' && (
                        <button type="button" className="ml-2 inline-flex items-center rounded p-0.5 text-ink-faint hover:text-ink" title="Corregir total de la semana" onClick={e => { e.stopPropagation(); setCorrigiendo(l); }}><Pencil size={11} /></button>
                      )}
                    </td>
                    <td className={`${tdCls} text-right font-mono`}>{l.dias}</td>
                    <td className={`${tdCls} text-right font-mono font-semibold text-ink`}>{fmtH(l.horas_total)}</td>
                    <td className={`${tdCls} text-right font-mono`}>{fmtH(l.horas_ordinarias)}</td>
                    <td className={`${tdCls} text-right font-mono`} style={{ color: Number(l.horas_extra) > 0 ? '#f59e0b' : undefined }}>{fmtH(l.horas_extra)}</td>
                    <td className={`${tdCls} text-right font-mono`} style={{ color: Number(l.horas_gratificacion) > 0 ? '#a78bfa' : undefined }}>{fmtH(l.horas_gratificacion)}</td>
                    <td className={`${tdCls} text-right font-mono`} style={{ color: Number(l.tarifa_hora) > 0 ? undefined : '#f59e0b' }}>{Number(l.tarifa_hora) > 0 ? fmtCRC(l.tarifa_hora) : 'Pendiente'}</td>
                    <td className={`${tdCls} text-right font-mono`}>{fmtCRC(l.monto_ordinario)}</td>
                    <td className={`${tdCls} text-right font-mono`}>{fmtCRC(l.monto_extra)}</td>
                    <td className={`${tdCls} text-right font-mono`}>{fmtCRC(l.monto_gratificacion)}</td>
                    <td className={`${tdCls} text-right font-mono`} title={Number(l.dias_incapacidad) > 0 ? `${l.dias_incapacidad} día(s), ${fmtH(l.horas_incapacidad)} h` : undefined}
                      style={{ color: Number(l.monto_incapacidad) > 0 ? '#f59e0b' : undefined }}>{fmtCRC(l.monto_incapacidad)}</td>
                    <td className={`${tdCls} text-right font-mono font-semibold text-ink`}>{fmtCRC(l.total_devengado)}</td>
                  </tr>
                  {isExp && dias && (() => {
                    const sumH = dias.reduce((s, d) => s + d.horas, 0);
                    const esOp = l.es_operador;
                    const resOrd = Number(l.horas_ordinarias);
                    const resExt = Number(l.horas_extra);
                    const resGrat = Number(l.horas_gratificacion);
                    return (
                    <tr>
                      <td colSpan={13} className="px-4 pb-3 pt-1" style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <div className="flex flex-wrap gap-2">
                          {dias.map(d => {
                            const tiene = d.horas > 0 || !!d.marca;
                            return (
                              <div key={d.fecha} className="rounded px-2.5 py-1.5 text-xs" style={{ minWidth: '5.5rem', background: tiene ? 'rgba(255,255,255,0.05)' : 'transparent' }}>
                                <div className="mb-0.5 text-[10px] font-semibold uppercase text-ink-faint">{diaSemana(d.fecha)} {d.fecha.slice(8)}/{d.fecha.slice(5, 7)}</div>
                                {d.marca && d.horas === 0 ? (
                                  <div className="font-mono text-amber-400">{d.marca}</div>
                                ) : d.horas > 0 ? (
                                  <>
                                    <div className="font-mono text-ink">{fmtH(d.horas)}</div>
                                    <div className="font-mono text-[10px]">
                                      <span className="text-ink-muted">{fmtH(d.nor)} N</span>
                                      {d.ext > 0 && <span className="ml-1 text-amber-400">+ {fmtH(d.ext)} E</span>}
                                    </div>
                                  </>
                                ) : (
                                  <div className="font-mono text-ink-faint">—</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {esOp && (
                          <div className="mt-2 rounded px-2.5 py-1.5 text-xs font-mono" style={{ background: 'rgba(255,255,255,0.04)' }}>
                            <span className="text-ink-muted">Boletas <span className="text-ink font-semibold">{fmtH(sumH)}</span></span>
                            <span className="text-ink-faint"> → </span>
                            <span className="text-ink">{fmtH(resOrd)} HN</span>
                            <span className="mx-1 text-ink-faint">·</span>
                            <span style={{ color: '#f59e0b' }}>{fmtH(resExt)} HE</span>
                            {resGrat > 0 && <>
                              <span className="mx-1 text-ink-faint">·</span>
                              <span style={{ color: '#a78bfa' }}>{fmtH(resGrat)} Gratif</span>
                            </>}
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })()}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-ink-faint">
        <CalendarCheck size={12} className="mr-1 inline" />
        Regla de finca: por día, hasta la jornada ordinaria son horas ordinarias y el resto extra a 1,5 (sumando todas las boletas del día de cada persona).
        Incapacidad: subsidio del 50 % de la jornada ordinaria los 3 primeros días de cada incapacidad, sin cargas sociales.
        Operadores de máquina: su total semanal se convierte a 48 h (6 × diario) + extras a su hora extra (diario ÷ 8 × 1,5) más sus extra reales; máximo 12 h extra y el resto gratificación. Al aprobar, la planilla llega armada a Planilla del ERP, que aplica CCSS, Banco Popular y renta.
      </p>

      {corrigiendo && (
        <CorregirTotal linea={corrigiendo} onCerrar={() => setCorrigiendo(null)} onGuardar={(t, o) => void corregirTotal(corrigiendo, t, o)} />
      )}
      {excluidas && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4" onMouseDown={() => setExcluidas(null)}>
          <div className={`${cardCls} max-h-[85vh] w-full max-w-4xl overflow-auto p-5 shadow-2xl`} onMouseDown={e => e.stopPropagation()}>
            <div className="mb-1 text-sm font-semibold text-ink">Líneas que quedaron fuera del cierre ({excluidas.length})</div>
            <p className="mb-3 text-xs text-ink-muted">Son labores que no se pagan por hora. Para que entren, la labor debe ser "por hora" en el catálogo, o la línea se corrige en la boleta.</p>
            {excluidas.length === 0 ? (
              <div className="py-6 text-center text-sm text-ink-muted">No se encontraron líneas (puede que ya se hayan corregido: use "Recalcular").</div>
            ) : (
              <div className={tableWrapCls}>
                <table className="w-full text-left text-sm">
                  <thead className={theadCls}><tr>
                    <th className={thCls}>Fecha</th><th className={thCls}>Boleta</th><th className={thCls}>Trabajador</th>
                    <th className={thCls}>Labor</th><th className={thCls}>Modalidad</th><th className={`${thCls} text-right`}>Horas</th><th className={`${thCls} text-right`}>Monto</th>
                  </tr></thead>
                  <tbody>{excluidas.map((x, i) => (
                    <tr key={i} className={trCls}>
                      <td className={tdCls}>{fmtFecha(x.fecha)}</td><td className={`${tdCls} font-mono`}>{x.boleta}</td>
                      <td className={`${tdCls} font-semibold text-ink`}>{x.trabajador}</td><td className={tdCls}>{x.labor}</td>
                      <td className={tdCls}>{x.modalidad}</td>
                      <td className={`${tdCls} text-right font-mono`}>{fmtH(x.horas)}</td><td className={`${tdCls} text-right font-mono`}>{fmtCRC(x.monto)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            <div className="mt-4 flex justify-end"><button type="button" className={btnSecondary} onClick={() => setExcluidas(null)}>Cerrar</button></div>
          </div>
        </div>,
        document.body,
      )}
      {confirmar && (
        <Confirmar
          titulo={confirmar.titulo}
          texto={confirmar.texto}
          peligro={confirmar.peligro}
          onNo={() => setConfirmar(null)}
          onSi={() => { const a = confirmar.accion; setConfirmar(null); a(); }}
        />
      )}
    </div>
  );
}
